import { Core } from "@faire/design-tokens";
import { Span } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { TermsAndPrivacyPolicyDisclaimer } from "@faire/retailer/components/Legal/TermsAndPrivacyPolicyDisclaimer";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getSettingFaireDirectCreditExpiresInDays } from "@faire/retailer/settings/getSettingFaireDirectCreditExpiresInDays";

import {
  AppSignUpModalVariant,
  isFaireDirectVariant,
} from "../AppSignUpModalVariant";

interface IProps {
  modalVariant: AppSignUpModalVariant;
  textAlign?: "left" | "center";
}

export const Disclaimer: React.FC<IProps> = ({ modalVariant, textAlign }) => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const brandCompanyName = faireDirectBrand?.name;
  const {
    isFaireDirectCreditPercentageBased,
    formattedMaxPercentageDiscountAmount,
  } = useFaireDirectFirstOrderIncentive();

  const getFaireDirectDisclaimerLine = () => {
    if (isFaireDirectCreditPercentageBased) {
      return (
        <StrictLocalMsg
          defaultMessage="*Available for retailers who are new to Faire. Max discount of {maximumDiscountMoney} & 1 year of free shipping with {brandCompanyName} only. Expires {expiresInDaysNumber} days after signup."
          description={{
            text: "Disclaimer for the Faire Direct signup credit",
            img: "https://cdn.faire.com/fastly/23a39450c40259b63373709fd9e039e422665966d9b78a0602b36bedc0312b42.png",
          }}
          values={{
            maximumDiscountMoney: formattedMaxPercentageDiscountAmount,
            brandCompanyName: brandCompanyName ?? "",
            expiresInDaysNumber: getSettingFaireDirectCreditExpiresInDays(),
          }}
        />
      );
    }
    if (
      modalVariant !== AppSignUpModalVariant.FAIRE_DIRECT_EMAIL_INVITATION &&
      brandCompanyName
    ) {
      return (
        <StrictLocalMsg
          defaultMessage="*You must be new to Faire and meet the order minimum for {brandCompanyName}. No other conditions apply."
          description={{
            text: "Disclaimer stating the only 2 conditions that need to be met",
            img: "https://cdn.faire.com/fastly/5f591e6440c10c6bced748e368fe82e7b9fd74ddc67eabdd00701eb8257ea54b.png",
          }}
          values={{ brandCompanyName }}
        />
      );
    } else {
      return (
        <StrictLocalMsg
          defaultMessage="*You must be new to Faire and meet the order minimum for the brand. No other conditions apply."
          description={{
            text: "Disclaimer stating the only 2 conditions that need to be met",
          }}
        />
      );
    }
  };

  const isFaireDirect = isFaireDirectVariant(modalVariant);

  return (
    <Span
      variant="labelSansRegular"
      color={Core.text.subdued}
      align={isFaireDirect ? "start" : textAlign ?? "center"}
    >
      {/* Extra syntax needed for the space after the fdDisclaimerLine */}
      {isFaireDirect ? <>{getFaireDirectDisclaimerLine()} </> : null}
      <TermsAndPrivacyPolicyDisclaimer />
    </Span>
  );
};
